import './CustomNativeDocsAccordion.scss';
import React, {useEffect, useRef} from 'react'; // TIFF/PDF A11Y CHANGE: useRef/useEffect manage read-only focus inside Natively Produced Docs without changing reusable components.
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/delete.svg';
import ArrButton from '../../../../common/arr-button/ArrButton';
import TagsSection from '../tags-section/TagsSection';
import TextPropertyToolbar from '../text-property-toolbar/TextPropertyToolbar';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {TagItem, FileTypeItem} from '../../../../../api/types/production.type';
import {ISelectItem} from '../../../../../shared/types';
import {t} from 'i18next';
import CustomFileTypeSelector from '../custom-file-type-selector/CustomFileTypeSelector';

export interface NativelyDocs {
  placeholderText: string;
  selectedTags: TagItem[];
  disabledTags?: TagItem[];
  brandingFont: string;
  brandingFontStyle: string;
  brandingFontSize: number;
  fileTypes: FileTypeItem[];
}

interface CustomNativeDocsAccordionProps {
  id: string;
  index: number;
  nativeDocsData: NativelyDocs;
  onDelete: (id: string) => void;
  onNativeDocsDataChange: (id: string, data: NativelyDocs) => void;
  tagsTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  insertFieldTypes: ISelectItem[];
  metadataFieldOptions: ISelectItem[];
  productionFieldOptions: ISelectItem[];
  placeholder?: string;
  disabled?: boolean;
}

const CustomNativeDocsAccordion: React.FC<CustomNativeDocsAccordionProps> = ({
  id,
  index,
  nativeDocsData,
  onDelete,
  onNativeDocsDataChange,
  tagsTreeData = [],
  insertFieldTypes,
  metadataFieldOptions,
  productionFieldOptions,
  placeholder,
  disabled = false,
}) => {
  const nativeDocsDetailsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!disabled) {
      return;
    }

    let animationFrameId = 0;

    const getControlText = (element: HTMLElement): string =>
      (element.getAttribute('aria-label') || element.textContent || '')
        .trim()
        .replace(/\s+/g, ' ');

    const setNonFocusable = (element: HTMLElement | null | undefined) => {
      if (!element) {
        return;
      }
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-disabled', 'true');
    };

    const setFocusable = (
      element: HTMLElement | null | undefined,
      ariaLabel?: string
    ) => {
      if (!element) {
        return;
      }
      element.setAttribute('tabindex', '0');
      element.setAttribute('aria-disabled', 'true');
      if (ariaLabel) {
        element.setAttribute('aria-label', ariaLabel);
      }
    };

    const updateReadOnlyFocusOrder = () => {
      const detailsElement = nativeDocsDetailsRef.current;
      if (!detailsElement) return;

      detailsElement
        .querySelectorAll<HTMLElement>(
          '[tabindex], button, input, select, textarea, a[href], [role="button"], [role="combobox"], [role="listbox"], [role="option"]'
        )
        .forEach(setNonFocusable);

      detailsElement
        .querySelectorAll<HTMLElement>(
          '[data-testid^="filtered-selected-tags-chip-"]'
        )
        .forEach((chipElement) => {
          const chipText = getControlText(chipElement);
          if (chipText) {
            chipElement
              .querySelectorAll<HTMLElement>(
                'button, [role="button"], [tabindex]'
              )
              .forEach((nestedElement) => {
                if (nestedElement !== chipElement) {
                  setNonFocusable(nestedElement);
                }
              });
            setFocusable(chipElement, chipText);
          }
        });
    };

    const scheduleFocusUpdate = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(updateReadOnlyFocusOrder);
    };

    scheduleFocusUpdate();

    const observer = new MutationObserver(scheduleFocusUpdate);
    if (nativeDocsDetailsRef.current) {
      observer.observe(nativeDocsDetailsRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['tabindex', 'disabled', 'aria-disabled', 'value'],
      });
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      observer.disconnect();
    };
  }, [
    disabled,
    nativeDocsData.selectedTags,
    nativeDocsData.fileTypes,
    nativeDocsData.placeholderText,
  ]);
  useEffect(() => {
    const detailsElement = nativeDocsDetailsRef.current;

    if (!detailsElement || disabled) return;

    const removableControls = Array.from(
      detailsElement.querySelectorAll<HTMLElement>(
        'button, [role="button"], [aria-label*="Remove"]'
      )
    );

    const getDisplayName = (item: unknown): string => {
      const record = item as Record<string, unknown>;
      return String(
        record.fileTypeLabel ??
          record.tagLabel ??
          record.name ??
          record.text ??
          record.value ??
          ''
      );
    };

    const selectedTagLabels = nativeDocsData.selectedTags
      .map(getDisplayName)
      .filter(Boolean);
    const selectedFileTypeLabels = nativeDocsData.fileTypes
      .filter((fileType) => fileType.isFileTypeSelected)
      .map(getDisplayName)
      .filter(Boolean);

    [...selectedFileTypeLabels, ...selectedTagLabels].forEach((label) => {
      const matchingControl = removableControls.find((control) =>
        control.textContent?.includes(label)
      );

      matchingControl?.setAttribute('aria-label', `Remove ${label}`);
    });
  }, [disabled, nativeDocsData.selectedTags, nativeDocsData.fileTypes]);

  const handleTagsChange = (tags: TagItem[]) => {
    if (disabled) return;
    onNativeDocsDataChange(id, {
      ...nativeDocsData,
      selectedTags: tags,
    });
  };

  const handleTextChange = (text: string) => {
    if (disabled) return;
    onNativeDocsDataChange(id, {
      ...nativeDocsData,
      placeholderText: text,
    });
  };
  const handleFileTypesChange = (fileTypesUpdated: FileTypeItem[]) => {
    if (disabled) return;
    onNativeDocsDataChange(id, {
      ...nativeDocsData,
      fileTypes: fileTypesUpdated,
    });
  };
  return (
    <Box className="custom-native-docs-accordion-wrapper">
      <Box className="custom-native-docs-header-wrapper">
        <Accordion className="custom-native-docs-accordion" defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className="custom-native-docs-accordion-summary"
            aria-label={`Natively Produced Docs ${index + 1} collapse toggle`}
          >
            <Typography
              className="typo-text subhead4_size custom-native-docs-title"
              data-testid={`custom-native-docs-accordion-title`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.nativelyProducedDocs.title',
                {number: index + 1}
              )}
            </Typography>
          </AccordionSummary>

          <AccordionDetails
            className="custom-native-docs-details"
            ref={nativeDocsDetailsRef}
          >
            <Box className="custom-native-docs-content">
              {/* File Types Selector */}
              <CustomFileTypeSelector
                onFileTypesChange={handleFileTypesChange}
                fileTypes={nativeDocsData.fileTypes}
                disabled={disabled}
                checkboxDataTestId={`custom-native-docs-file-type-selector-${id}`}
              />
              {/* Tags Section */}
              <TagsSection
                selectedTags={nativeDocsData.selectedTags}
                onTagsChange={handleTagsChange}
                tagsTreeData={tagsTreeData}
                disabledTags={nativeDocsData.disabledTags}
                disabled={disabled}
              />

              {/* Text Property Toolbar */}
              <TextPropertyToolbar
                variant="with-title"
                title="Placeholder Text"
                dataTestId="native-docs-data"
                toolbarText={nativeDocsData.placeholderText}
                insertFieldTypes={insertFieldTypes}
                metadataFieldOptions={metadataFieldOptions}
                productionFieldOptions={productionFieldOptions}
                onToolbarTextChange={handleTextChange}
                onInsertField={(field) => {
                  handleTextChange(
                    `${nativeDocsData.placeholderText}$${field}$`
                  );
                }}
                placeholder={placeholder}
                disabled={disabled}
                textAreaTestId={`native-docs-placeholder-${index}`}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
        {!disabled && (
          <ArrButton
            variant="secondary"
            size="small"
            onClick={() => onDelete(id)}
            className="delete-btn"
            dataTestId="custom-native-docs-accordion-button"
          >
            <DeleteIcon />
          </ArrButton>
        )}
      </Box>
    </Box>
  );
};

export default CustomNativeDocsAccordion;
